import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BellAlertIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  WrenchScrewdriverIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminCommandCenter } from '../../hooks/useAdminCommandCenter';
import {
  type AdminMetric,
  type AdminRealProviderPreview,
} from '../../services/adminCommandCenterService';
import {
  REPAIR_STATUS_ORDER,
  getRepairRequestStatusLabel,
} from '../../utils/repairRequestWorkflow';
import { RepairStatus } from '../../types/firestore';

type ProviderStatusFilter =
  | 'all'
  | 'approved'
  | 'pending_approval'
  | 'changes_requested'
  | 'profile_incomplete';
type ProviderKindFilter = 'real' | 'test' | 'all';
type CompletenessFilter = 'all' | 'complete' | 'incomplete';

const formatMetricValue = (metric: AdminMetric): string =>
  metric.value === null ? '—' : String(metric.value);

const SectionHeader: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
  error?: string;
}> = ({ eyebrow, title, description, error }) => (
  <div className="mb-3 flex flex-col gap-1 sm:mb-4">
    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-200">
      {eyebrow}
    </p>
    <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-base font-semibold text-primary-light-100 sm:text-lg">{title}</h2>
        <p className="mt-1 max-w-2xl text-sm text-primary-light-400">{description}</p>
      </div>
      {error && (
        <span className="rounded-md border border-status-error-500/35 bg-status-error-500/10 px-2 py-1 text-xs text-status-error-300">
          Błąd odczytu
        </span>
      )}
    </div>
  </div>
);

const MetricCard: React.FC<{
  metric: AdminMetric;
  tone?: 'default' | 'attention' | 'good' | 'warning';
}> = ({ metric, tone = 'default' }) => {
  const toneClass =
    tone === 'attention'
      ? 'border-status-error-500/35 bg-status-error-500/10'
      : tone === 'good'
        ? 'border-ratuj-300/25 bg-ratuj-300/10'
        : tone === 'warning'
          ? 'border-amber-300/25 bg-amber-300/10'
          : 'border-primary-dark-300/18 bg-primary-dark-100/10';

  return (
    <article className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-primary-light-200">{metric.label}</p>
        {metric.partial && (
          <span className="rounded-md border border-amber-300/25 px-1.5 py-0.5 text-[10px] font-medium text-amber-200">
            partial
          </span>
        )}
      </div>
      <p className="mt-2 text-2xl font-semibold text-primary-light-100">
        {formatMetricValue(metric)}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-primary-light-500" title={metric.description}>
        {metric.description}
      </p>
    </article>
  );
};

const QueueCard: React.FC<{
  title: string;
  metric: AdminMetric;
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}> = ({ title, metric, to, icon: Icon }) => {
  const hasWork = (metric.value ?? 0) > 0;
  return (
    <Link
      to={to}
      className={`group rounded-lg border p-4 transition-colors ${
        hasWork
          ? 'border-ratuj-300/30 bg-ratuj-300/8 hover:border-ratuj-200/45'
          : 'border-primary-dark-300/18 bg-primary-dark-100/10 hover:border-primary-dark-300/32'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-primary-light-100">{title}</p>
          <p className="mt-1 line-clamp-2 text-xs text-primary-light-400">{metric.description}</p>
        </div>
        <Icon className="h-5 w-5 flex-shrink-0 text-ratuj-200" />
      </div>
      <div className="mt-4 flex items-end justify-between gap-3">
        <p className="text-3xl font-semibold text-primary-light-100">{formatMetricValue(metric)}</p>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-ratuj-200 group-hover:text-ratuj-100">
          Otwórz
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
};

const completenessLabel = (provider: AdminRealProviderPreview): string => {
  const missing = [
    provider.completeness.hasLocation ? null : 'lokalizacja',
    provider.completeness.hasProfileImage ? null : 'zdjęcie',
    provider.completeness.hasBusinessHours ? null : 'godziny',
    provider.completeness.hasServiceFulfillment ? null : 'realizacja',
    provider.completeness.hasSpecializations ? null : 'specjalizacje',
  ].filter(Boolean);
  return missing.length === 0 ? 'Kompletny' : `Braki: ${missing.join(', ')}`;
};

const isProviderComplete = (provider: AdminRealProviderPreview): boolean =>
  provider.completeness.hasLocation &&
  provider.completeness.hasProfileImage &&
  provider.completeness.hasBusinessHours &&
  provider.completeness.hasServiceFulfillment &&
  provider.completeness.hasSpecializations;

const ProviderRow: React.FC<{ provider: AdminRealProviderPreview }> = ({ provider }) => (
  <article className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3">
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="min-w-0 max-w-full break-words text-sm font-semibold text-primary-light-100 lg:max-w-sm lg:truncate">
            {provider.serviceName}
          </h3>
          {provider.isOperational && (
            <span className="rounded-md bg-ratuj-300/15 px-2 py-0.5 text-[11px] font-medium text-ratuj-100">
              Operacyjny
            </span>
          )}
          {provider.isTestData && (
            <span className="rounded-md bg-primary-dark-100 px-2 py-0.5 text-[11px] font-medium text-primary-light-400">
              Testowy
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-primary-light-400">
          {provider.city || 'Brak miasta'} · status: {provider.verificationStatus} ·{' '}
          {provider.isVerified ? 'isVerified=true' : 'isVerified=false'}
        </p>
        <p className="mt-1 text-xs text-primary-light-500">
          {completenessLabel(provider)} · aktywne requesty: {provider.activeRequestsLabel}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {provider.publicProfilePath && (
          <Link
            to={provider.publicProfilePath}
            className="rounded-lg border border-primary-dark-200 px-3 py-1.5 text-xs font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
          >
            Profil publiczny
          </Link>
        )}
        {provider.verificationPath && (
          <Link
            to={provider.verificationPath}
            className="rounded-lg border border-ratuj-300/35 px-3 py-1.5 text-xs font-medium text-ratuj-100 hover:border-ratuj-200/60"
          >
            Weryfikacja
          </Link>
        )}
      </div>
    </div>
  </article>
);

const AdminDashboardPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { data, loading, error, refresh } = useAdminCommandCenter();
  const [statusFilter, setStatusFilter] = useState<ProviderStatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<ProviderKindFilter>('real');
  const [cityFilter, setCityFilter] = useState('');
  const [completenessFilter, setCompletenessFilter] = useState<CompletenessFilter>('all');

  const filteredProviders = useMemo(() => {
    const normalizedCity = cityFilter.trim().toLowerCase();
    return (data?.realProvidersPreview ?? []).filter((provider) => {
      if (statusFilter !== 'all' && provider.verificationStatus !== statusFilter) return false;
      if (kindFilter === 'real' && provider.isTestData) return false;
      if (kindFilter === 'test' && !provider.isTestData) return false;
      if (normalizedCity && !(provider.city || '').toLowerCase().includes(normalizedCity)) {
        return false;
      }
      if (completenessFilter === 'complete' && !isProviderComplete(provider)) return false;
      if (completenessFilter === 'incomplete' && isProviderComplete(provider)) return false;
      return true;
    });
  }, [cityFilter, completenessFilter, data?.realProvidersPreview, kindFilter, statusFilter]);

  const primaryNumbers = data
    ? [data.requestStats.active, data.providerStats.operational, data.queues.pendingVerifications]
    : [];

  if (loading && !data) {
    return (
      <section className="space-y-4">
        <div className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-5">
          <div className="h-5 w-48 animate-pulse rounded bg-primary-dark-300" />
          <div className="mt-3 h-4 w-full max-w-xl animate-pulse rounded bg-primary-dark-300/75" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((item) => (
            <div
              key={item}
              className="h-32 animate-pulse rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="rounded-lg border border-status-error-500/40 bg-status-error-500/10 p-5 text-status-error-200">
        <h1 className="text-base font-semibold">Centrum operacyjne niedostępne</h1>
        <p className="mt-2 text-sm text-status-error-300">{error}</p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-ratuj-400 px-3 py-2 text-sm font-medium text-white hover:bg-ratuj-300"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Ponów odczyt
        </button>
      </section>
    );
  }

  if (!data) return null;

  return (
    <section className="space-y-4 sm:space-y-5">
      <header className="border-b border-primary-dark-300/18 px-1 pb-4 sm:px-0 sm:pb-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-200">
              Admin Command Center V1
            </p>
            <h1 className="mt-1 text-xl font-semibold text-primary-light-100 sm:text-2xl">
              Centrum operacyjne
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-primary-light-300">
              Witaj{userProfile?.displayName ? `, ${userProfile.displayName}` : ''}. Ten widok
              pokazuje kondycję platformy, kolejki decyzji, realne serwisy i ryzyka danych bez
              tworzenia drugiego źródła prawdy.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-primary-light-400">
            <span className="rounded-lg border border-primary-dark-300 px-2.5 py-1.5">
              Odczyt: {data.readAt.toLocaleString('pl-PL')}
            </span>
            {data.isPartial && (
              <span className="rounded-lg border border-amber-300/30 bg-amber-300/10 px-2.5 py-1.5 text-amber-100">
                Dane częściowe
              </span>
            )}
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-dark-300 px-2.5 py-1.5 text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Odśwież
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {primaryNumbers.map((entry) => (
            <MetricCard
              key={entry.label}
              metric={entry}
              tone={entry.label === 'Operacyjne' ? 'good' : 'default'}
            />
          ))}
        </div>
      </header>

      {Object.keys(data.sectionErrors).length > 0 && (
        <div className="rounded-lg border border-status-error-500/40 bg-status-error-500/10 p-3 text-sm text-status-error-300">
          Część sekcji nie ma pełnego read contractu albo odczyt zwrócił błąd. Dashboard pokazuje
          pozostałe dane jawnie jako partial.
        </div>
      )}

      <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
        <SectionHeader
          eyebrow="Kolejki"
          title="Wymaga reakcji"
          description="Najkrótsza ścieżka do decyzji admina. Karty są nawigacją, nie mutacją."
          error={data.sectionErrors.queues}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <QueueCard
            title="Serwisy do weryfikacji"
            metric={data.queues.pendingVerifications}
            to="/admin/verifications"
            icon={ShieldCheckIcon}
          />
          <QueueCard
            title="Sugestie"
            metric={data.queues.activeSuggestions}
            to="/admin/suggestions"
            icon={LightBulbIcon}
          />
          <QueueCard
            title="Specjalizacje"
            metric={data.queues.pendingSpecializations}
            to="/admin/specializations"
            icon={WrenchScrewdriverIcon}
          />
          <QueueCard
            title="Typy sprzętu"
            metric={data.queues.pendingEquipmentTypes}
            to="/admin/equipment-types"
            icon={Squares2X2Icon}
          />
        </div>
      </section>

      <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
        <SectionHeader
          eyebrow="Kondycja"
          title="Statystyki platformy"
          description="Read-only metryki z repairRequests i serviceProviders. Statusy zgłoszeń używają aktualnej macierzy workflow."
          error={data.sectionErrors.requestStats || data.sectionErrors.providerStats}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard metric={data.requestStats.total} />
          <MetricCard metric={data.requestStats.active} tone="warning" />
          <MetricCard metric={data.requestStats.terminal} />
          <MetricCard
            metric={data.requestStats.problematic}
            tone={(data.requestStats.problematic.value ?? 0) > 0 ? 'attention' : 'default'}
          />
          <MetricCard metric={data.providerStats.total} />
          <MetricCard metric={data.providerStats.real} />
          <MetricCard metric={data.providerStats.test} />
          <MetricCard metric={data.providerStats.operational} tone="good" />
          <MetricCard metric={data.providerStats.visible} />
          <MetricCard metric={data.providerStats.pending} tone="warning" />
          <MetricCard metric={data.providerStats.changesRequested} tone="warning" />
          <MetricCard metric={data.providerStats.profileIncomplete} />
        </div>
        <div className="mt-4 rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary-light-200">
            <ChartBarIcon className="h-4 w-4 text-ratuj-200" />
            Statusy zgłoszeń
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {REPAIR_STATUS_ORDER.map((status) => {
              const metric = data.requestStats.byStatus[status];
              const tone =
                status === RepairStatus.NEW || status === RepairStatus.PENDING_QUOTE
                  ? 'warning'
                  : undefined;
              return (
                <div
                  key={status}
                  className="flex items-center justify-between gap-3 rounded-md border border-primary-dark-300/50 px-3 py-2"
                >
                  <span className="truncate text-xs text-primary-light-300">
                    {getRepairRequestStatusLabel(status)}
                  </span>
                  <span
                    className={`text-sm font-semibold ${
                      tone === 'warning' ? 'text-amber-100' : 'text-primary-light-100'
                    }`}
                  >
                    {formatMetricValue(metric)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
        <SectionHeader
          eyebrow="Serwisy"
          title="Realne serwisy"
          description={`Podgląd ograniczony do ostatnio aktualizowanych providerów (limit ${data.readLimits.providerPreviewLimit}); aktywne requesty per serwis nie są liczone w V1 bez taniego agregatu.`}
          error={data.sectionErrors.realProviders}
        />
        <div className="mb-3 flex justify-start">
          <Link
            to="/admin/service-providers"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ratuj-300/35 px-3 py-2 text-sm font-medium text-ratuj-100 hover:border-ratuj-200/60"
          >
            Zobacz wszystkie serwisy
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
        <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-4">
          <label className="text-xs text-primary-light-400">
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as ProviderStatusFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="approved">Approved</option>
              <option value="pending_approval">Weryfikacja</option>
              <option value="changes_requested">Do poprawek</option>
              <option value="profile_incomplete">Niekompletny</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Typ
            <select
              value={kindFilter}
              onChange={(event) => setKindFilter(event.target.value as ProviderKindFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="real">Realne</option>
              <option value="test">Testowe</option>
              <option value="all">Wszystkie</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Kompletność
            <select
              value={completenessFilter}
              onChange={(event) => setCompletenessFilter(event.target.value as CompletenessFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="complete">Kompletne</option>
              <option value="incomplete">Z brakami</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Miasto
            <div className="relative mt-1">
              <FunnelIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-primary-light-500" />
              <input
                value={cityFilter}
                onChange={(event) => setCityFilter(event.target.value)}
                placeholder="np. Warszawa"
                className="w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 py-2 pl-9 text-sm text-primary-light-100 placeholder:text-primary-light-500"
              />
            </div>
          </label>
        </div>
        <div className="space-y-2">
          {filteredProviders.length === 0 ? (
            <div className="rounded-lg border border-primary-dark-300/55 bg-primary-dark-300/25 p-4 text-sm text-primary-light-400">
              Brak realnych serwisów dla aktualnego filtra albo preview nie zwróciło dokumentów.
            </div>
          ) : (
            filteredProviders.map((provider) => (
              <ProviderRow key={provider.uid} provider={provider} />
            ))
          )}
        </div>
      </section>

      <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
        <SectionHeader
          eyebrow="Nawigacja"
          title="Moduły admina"
          description="Szybkie wejścia do istniejących hostów. Badge używają tego samego read-modelu co dashboard."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.moduleShortcuts.map((shortcut) => {
            const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
              verifications: ShieldCheckIcon,
              suggestions: LightBulbIcon,
              specializations: WrenchScrewdriverIcon,
              'equipment-types': Squares2X2Icon,
              notifications: BellAlertIcon,
              'service-providers': BuildingStorefrontIcon,
              tools: WrenchIcon,
            };
            const Icon = iconMap[shortcut.key] || ArrowRightIcon;
            return (
              <Link
                key={shortcut.key}
                to={shortcut.to}
                className="group rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-4 hover:border-ratuj-300/36"
              >
                <div className="flex items-start justify-between gap-3">
                  <Icon className="h-5 w-5 flex-shrink-0 text-ratuj-200" />
                  <span className="rounded-md border border-primary-dark-300 px-2 py-0.5 text-xs text-primary-light-300">
                    {shortcut.count === null
                      ? shortcut.countLabel
                      : `${shortcut.count} ${shortcut.countLabel}`}
                  </span>
                </div>
                <h3 className="mt-3 text-sm font-semibold text-primary-light-100">
                  {shortcut.label}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
                  {shortcut.description}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
        <SectionHeader
          eyebrow="Jakość danych"
          title="Ryzyka danych"
          description={`Read-only sygnały ryzyka. Braki profilu liczone są na widocznych realnych providerach w limicie ${data.readLimits.dataRiskProviderLimit}.`}
          error={data.sectionErrors.dataRisks}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <MetricCard
            metric={data.dataRisks.suspendedAccounts}
            tone={(data.dataRisks.suspendedAccounts.value ?? 0) > 0 ? 'attention' : 'default'}
          />
          <MetricCard
            metric={data.dataRisks.approvedWithoutLocation}
            tone={(data.dataRisks.approvedWithoutLocation.value ?? 0) > 0 ? 'warning' : 'default'}
          />
          <MetricCard
            metric={data.dataRisks.approvedWithoutProfileImage}
            tone={
              (data.dataRisks.approvedWithoutProfileImage.value ?? 0) > 0 ? 'warning' : 'default'
            }
          />
          <MetricCard
            metric={data.dataRisks.approvedWithoutBusinessHours}
            tone={
              (data.dataRisks.approvedWithoutBusinessHours.value ?? 0) > 0 ? 'warning' : 'default'
            }
          />
          <MetricCard
            metric={data.dataRisks.approvedWithoutServiceFulfillment}
            tone={
              (data.dataRisks.approvedWithoutServiceFulfillment.value ?? 0) > 0
                ? 'warning'
                : 'default'
            }
          />
          <MetricCard metric={data.dataRisks.providerUserInconsistencies} />
        </div>
        <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-primary-light-500">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-200" />
          Dashboard nie wykonuje backfillu, nie zgaduje brakujących pól legacy i nie rozszerza
          uprawnień. Jeśli metryka wymagałaby pełnego skanu albo joinu, pozostaje partial lub
          niedostępna w V1.
        </p>
      </section>

      <div className="flex items-center gap-2 text-xs text-primary-light-500">
        <CheckBadgeIcon className="h-4 w-4 text-ratuj-200" />
        Source of truth pozostaje w kolekcjach domenowych; brak nowych zapisów statystyk.
      </div>
    </section>
  );
};

export default AdminDashboardPage;
